import api from "@/api/axios";

/**
 * Creates a new bot object from an existing bot for copying purposes
 * @param {Object} botData - The original bot data
 * @param {string} accountId - The target account ID (optional, uses original if not provided)
 * @returns {Object} New bot object ready for creation
 */
export function createCopiedBotData(botData, accountId = null) {
  // Format today's date as "DD MMM YYYY"
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[today.getMonth()];
  const year = today.getFullYear();
  const dateStr = `${day} ${month} ${year}`;

  // Remove existing date appendage from name if it exists
  // Pattern matches: " (DD MMM YYYY)" at the end of the string
  const datePattern = / \(\d{1,2} \w{3} \d{4}\)$/;
  const baseName = botData.name.replace(datePattern, '');

  // Create a new bot object with copied settings but cleared unique fields
  return {
    ...botData,
    id: null, // Clear ID to indicate new bot
    name: `${baseName} (${dateStr})`, // Replace or append date to name
    status: "new",
    auto_start: false, // Default to not auto-starting
    strategy_config: { ...botData.strategy_config }, // Explicitly copy strategy configuration
    // Pre-populate API keys from the original bot (user can change in modal)
    api_key_id: botData.api_key_id,
    // Convert api_keys (read field) to api_key_ids (write field) for the form
    api_key_ids: botData.api_keys || [],
    account_id: accountId || botData.account_id, // Use provided account ID or original
    // Keep additional fields for display
    account_name: botData.account_name,
    api_key_description: botData.api_key_description,
    isNewBot: true, // Flag to indicate this is a copied bot
  };
}

/**
 * Fetches bot details and creates a copied bot object
 * @param {number} botId - The ID of the bot to copy
 * @param {string} accountId - The target account ID (optional)
 * @returns {Promise<Object>} Promise that resolves to the new bot object
 */
export async function fetchAndCopyBot(botId, accountId = null) {
  const response = await api.get(`/api/bots/${botId}/`);
  return createCopiedBotData(response.data, accountId);
}